import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import pkg from '../../package.json';
import {
  Box, Typography, Button, Alert, CircularProgress,
} from '@mui/material';
import InputForm from './InputForm';
import ResultsTable from './ResultsTable';
// eslint-disable-next-line no-unused-vars
import { defaultCustomerConfig, FOCUS_RING } from '../theme/theme';

const APP_VERSION = pkg.version;

const SkylineSvg = ({ color }) => (
  <Box sx={{ position: 'fixed', bottom: 0, right: 0, width: '40vw', maxWidth: 520, opacity: 0.06, pointerEvents: 'none', zIndex: 0 }}>
    <svg aria-hidden="true" viewBox="0 0 800 300" xmlns="http://www.w3.org/2000/svg" fill={color}>
      <rect x="0"   y="200" width="60"  height="100"/>
      <rect x="20"  y="160" width="20"  height="40"/>
      <rect x="70"  y="180" width="50"  height="120"/>
      <rect x="80"  y="150" width="30"  height="30"/>
      <rect x="130" y="140" width="80"  height="160"/>
      <rect x="150" y="100" width="40"  height="40"/>
      <rect x="155" y="80"  width="10"  height="20"/>
      <rect x="220" y="170" width="60"  height="130"/>
      <rect x="235" y="140" width="30"  height="30"/>
      <rect x="290" y="120" width="100" height="180"/>
      <rect x="310" y="80"  width="60"  height="40"/>
      <rect x="335" y="60"  width="10"  height="20"/>
      <rect x="400" y="160" width="70"  height="140"/>
      <rect x="415" y="130" width="40"  height="30"/>
      <rect x="480" y="100" width="90"  height="200"/>
      <rect x="500" y="60"  width="50"  height="40"/>
      <rect x="520" y="40"  width="10"  height="20"/>
      <rect x="580" y="150" width="60"  height="150"/>
      <rect x="595" y="120" width="30"  height="30"/>
      <rect x="650" y="130" width="80"  height="170"/>
      <rect x="665" y="90"  width="50"  height="40"/>
      <rect x="688" y="70"  width="10"  height="20"/>
      <rect x="740" y="180" width="60"  height="120"/>
      <rect x="750" y="150" width="40"  height="30"/>
      <rect x="0"   y="295" width="800" height="5"/>
    </svg>
  </Box>
);

export default function ProductionApp({ config, toggleLang, debugMode = false }) {
  const { t, i18n } = useTranslation();
  const [result, setResult]         = useState(null);
  const [formValues, setFormValues] = useState(null);
  const [loading, setLoading]       = useState(false);
  const [apiError, setApiError]     = useState(false);
  const handleResult = (data, fv) => {
    if (data === null && fv === null) {
      setResult(null); setFormValues(null); setApiError(false);
    } else if (data === null) {
      setResult(null); setFormValues(null); setApiError(true);
    } else if (data && data.specificResult) {
      setResult(data); setFormValues(fv); setApiError(false);
    } else {
      setApiError(true);
    }
  };

  return (
    <Box sx={{ backgroundColor: config.backgroundColor, position: 'relative', overflow: 'hidden' }}>
      <SkylineSvg color={config.primaryColor} />


      {/* ── Header banner — full width ── */}
      <Box sx={{ position: 'relative', width: '100%', zIndex: 1 }}>
        <Box component="img" src="/logos/More/more_banner_header.png" alt="מור גמל ופנסיה"
          sx={{ width: '100%', display: 'block', height: 80, objectFit: 'cover' }} />
        {/* Language toggle — physical left */}
        {/* 48px matches the disclaimer's margin, so the header lines up with the text column below */}
        <Box style={{ position: 'absolute', top: '50%', left: '48px', transform: 'translateY(-50%)' }}>
          <Button variant="outlined" size="small" onClick={toggleLang}
            aria-label={t('langToggleAria')}
            sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', '&:hover': { borderColor: '#fff' },
                  // A-2-Z row 14 — sits on the dark header banner, where the teal ring measured 1.21:1
                  '&:focus-visible': FOCUS_RING }}>
            {t('langToggle')}
          </Button>
        </Box>
        {/* MORE logo — separate transparent PNG, clickable */}
        <a
          href="https://www.moreinvest.co.il/"
          target="_blank"
          rel="noopener noreferrer"
          aria-label="מור גמל ופנסיה — אתר החברה"
          style={{ position: 'absolute', top: '50%', right: '48px', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center' }}
        >
          <img
            src="/logos/More/more_banner_header_logo.png"
            alt="MORE — מור גמל ופנסיה"
            style={{ height: '56px', width: 'auto' }}
          />
        </a>
      </Box>

      {/* ── Centered content column ── */}
      <Box component="main" sx={{ maxWidth: 1100, mx: 'auto', position: 'relative', zIndex: 1 }}>

        {/* Form + results */}
        {/* pt trimmed from 8px to 4px — invisible above the title, and it pays for the gap
            under the footer banner without pushing the page into scrolling */}
        <Box data-testid="form-column" sx={{ width: '100%', pt: 0.5, pb: 1, px: { xs: 1.5, md: 0 } }}>
          {apiError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setApiError(false)}>
              {t('apiError')}
            </Alert>
          )}
          <Box data-testid="form-content" sx={{ width: '100%' }}>
            <Box>
              <InputForm
                onResult={handleResult}
                onLoading={setLoading}
                config={config}
                selectedScenario=""
                debugMode={debugMode}
              />
            </Box>
            {loading ? (
              <Box role="status" aria-label={t('calculating')} sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress color="secondary" size={48} aria-hidden="true" />
              </Box>
            ) : (
              <Box sx={{ mt: 2 }}>
                <ResultsTable result={result} formValues={formValues} config={config} />
              </Box>
            )}
          </Box>
        </Box>

      </Box>

      {/* ── Disclaimer — full width ── */}
      {config.disclaimerText && (
        // pt gives the breathing room the designer asked for between the calculator and the
        // disclaimer. It is deliberately modest: the page must still fit without scrolling.
        <Box sx={{ px: { xs: 2, md: 6 }, pt: '16px', pb: '2px', backgroundColor: '#f5f5f5' }}
          style={{ direction: 'rtl', textAlign: 'justify' }}>
          <Typography component="h2" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0,0,0,0)', whiteSpace: 'nowrap' }}>
            הערות משפטיות
          </Typography>
          <Typography variant="body2" style={{ fontSize: '0.75rem', color: '#4a5568', lineHeight: 1.4, direction: 'rtl', textAlign: 'justify', display: 'block' }}>
            {config.disclaimerText.replace(/\s*\n+\s*/g, ' ').trim()}
          </Typography>
        </Box>
      )}

      {/* ── Footer banner — full width ── */}
      <Box sx={{ height: 80, position: 'relative',
        backgroundImage: 'url(/logos/More/more_banner_footer.png)',
        backgroundSize: 'cover', backgroundPosition: 'center',
      }}>
        {/* Accessibility statement — physical right, on the 48px line of the disclaimer.
            The designer asked for it under the disclaimer; it lives here instead because an
            extra line there pushes the page into scrolling, which is the harder constraint. */}
        <Box style={{ position: 'absolute', top: '50%', right: '48px', transform: 'translateY(-50%)' }}>
          <Typography
            component="a"
            href="https://www.moreinvest.co.il/%D7%9E%D7%99%D7%93%D7%A2-%D7%A0%D7%95%D7%A1%D7%A3/%D7%94%D7%A6%D7%94%D7%A8%D7%AA-%D7%A0%D7%92%D7%99%D7%A9%D7%95%D7%AA/"
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('accessibilityStatementAria')}
            sx={{ fontSize: '12px', color: '#E6ECF2', textDecoration: 'underline',
                  textUnderlineOffset: '3px', '&:hover': { color: '#FFFFFF' },
                  '&:focus-visible': FOCUS_RING }}
          >
            {t('accessibilityStatement')}
          </Typography>
        </Box>
        <Box style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', display: 'flex', gap: '10px', alignItems: 'center' }}>
          {[
            { href: 'https://www.instagram.com/more_invest/',                    src: '/logos/More/instagram.svg', label: 'אינסטגרם' },
            { href: 'https://www.linkedin.com/company/more-investment-house/',   src: '/logos/More/linkedin.svg',  label: 'לינקדאין' },
            { href: 'https://www.youtube.com/channel/UCnpV3O5vH4mcCSJY8Bb0OFQ', src: '/logos/More/youtube.svg',   label: 'יוטיוב'   },
            { href: 'https://www.facebook.com/moreinvest.co.il',                 src: '/logos/More/facebook.svg',  label: 'פייסבוק'  },
          ].map(({ href, src, label }) => (
            <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
              style={{ display: 'flex', alignItems: 'center' }}>
              <img src={src} alt={label} style={{ height: 38, width: 'auto' }} />
            </a>
          ))}
        </Box>
      </Box>

      {/* ── Powered by — full width ── */}
      {/* py trimmed to 0 — the height it gives back pays for the gap above the disclaimer,
          so the page still fits on one screen without scrolling. */}
      <Box sx={{ pt: '6px', pb: '2px', textAlign: 'center', backgroundColor: config.backgroundColor }}>
        <Typography variant="body2" sx={{ fontSize: '11px', lineHeight: 1.2, color: '#4a6070' }}>
          {i18n.language === 'he'
            ? `כלי זה פותח ומופעל על ידי חברת אמפלייר טכנולוגיות בע"מ עבור מור גמל ופנסיה © ${new Date().getFullYear()} | version ${APP_VERSION}`
            : `This tool was developed and is operated by MPLAIER Technologies Ltd for Mor Pension & Provident © ${new Date().getFullYear()} | version ${APP_VERSION}`}
        </Typography>
      </Box>

    </Box>
  );
}
